import { useState, useEffect, useCallback } from 'react';
import { App as CapApp } from '@capacitor/app';
import { isNative } from '../utils/platformUtils';
import { claimPatreon, parseNativeReturn, readWebReturn, isPatreonEnabled } from '../lib/patreon';
import { track } from '../utils/analytics';

// Retour dans l'app apres l'autorisation Patreon, sur les deux plateformes :
//  - web   : le serveur a redirige sur `/?patreon=<nonce>` ;
//  - natif : la page interstitielle a saute vers `upscale://patreon?patreon=...`,
//            recu ici par le listener `appUrlOpen`.
//
// Dans les deux cas on reclame le nonce avec le JWT courant. C'est cette requete
// qui lie le compte Patreon a l'utilisateur, et sa reponse deverrouille l'UI
// sans attendre le prochain pull de sync.
//
// Rend un message a afficher (deverrouillage, ou refus explique), ou null.
//
// Natif, deux livraisons possibles pour la meme URL :
//  - a chaud : l'app tournait encore, appUrlOpen se declenche ;
//  - a froid : iOS a tue l'app pendant l'autorisation (le SFSafariViewController prend la
//              memoire), le retour relance l'app et l'URL n'arrive QUE par getLaunchUrl().
//              Sans cette lecture, le lien Patreon est perdu sans que l'utilisateur le sache.

const MESSAGES = {
  denied: null, // l'utilisateur a annule sur la page Patreon : rien a dire.
  invalid_state: 'This Patreon link expired. Please try again.',
  exchange_failed: 'Patreon did not respond. Please try again.',
  invalid_nonce: 'This Patreon link was already used. Please try again.',
};

// Un nonce est a usage unique : rejoue, claimPatreon repond 400 et l'utilisateur voit
// "already used" alors que son compte vient d'etre lie. Deux gardes, une par facon de
// le rejouer.

// 1. Meme URL livree deux fois dans la meme session JS (getLaunchUrl + appUrlOpen au
//    demarrage a froid). Au niveau module, donc partagee par tous les montages du hook.
const handledUrls = new Set();

// 2. getLaunchUrl() rend l'URL de lancement indefiniment, y compris apres un reload et
//    y compris sans rapport avec Patreon : on ne la lit qu'une fois par session de
//    webview. sessionStorage meurt avec la webview, donc un vrai lancement a froid
//    repart bien de zero.
const LAUNCH_READ_KEY = 'upscale_patreon_launch_read';

function claimUrl(url) {
  if (handledUrls.has(url)) return false;
  handledUrls.add(url);
  return true;
}

function claimLaunchRead() {
  try {
    if (sessionStorage.getItem(LAUNCH_READ_KEY)) return false;
    sessionStorage.setItem(LAUNCH_READ_KEY, '1');
  } catch {
    // Storage indisponible : le dedoublonnage par URL reste actif.
  }
  return true;
}

export function usePatreonReturn() {
  const [notice, setNotice] = useState(null); // { severity, message }
  const [pending, setPending] = useState(false);

  const handle = useCallback(async (ret) => {
    if (!ret) return;
    if (ret.error) {
      const message = MESSAGES[ret.error] ?? MESSAGES.exchange_failed;
      if (message) setNotice({ severity: 'error', message });
      track('patreon_link_failed', { reason: ret.error });
      return;
    }
    if (!ret.nonce) return;

    setPending(true);
    try {
      const res = await claimPatreon(ret.nonce);
      track('patreon_linked', { eligible: !!res.eligible });
      setNotice(
        res.eligible
          ? { severity: 'success', message: 'Patreon connected. Everything is unlocked.' }
          : {
              severity: 'warning',
              // Aucune mention de prix ni de tier : guideline App Store 3.1.3(b).
              message: 'Your Patreon account is connected, but your membership does not include upScale.',
            }
      );
    } catch (err) {
      track('patreon_link_failed', { reason: err.status === 400 ? 'invalid_nonce' : 'network' });
      setNotice({ severity: 'error', message: MESSAGES.invalid_nonce });
    } finally {
      setPending(false);
    }
  }, []);

  useEffect(() => {
    if (!isPatreonEnabled()) return undefined;

    // Web : le nonce est dans l'URL au premier rendu. readWebReturn la nettoie.
    if (!isNative()) {
      handle(readWebReturn());
      return undefined;
    }

    // Natif : deep link. Browser.close() referme le SFSafariViewController reste
    // ouvert par-dessus l'app.
    const consume = async (url) => {
      const ret = parseNativeReturn(url);
      // Une URL de lancement etrangere a Patreon (deep link contenu, notification) s'arrete ici.
      if (!ret || !claimUrl(url)) return;
      const { Browser } = await import('@capacitor/browser');
      Browser.close().catch(() => {});
      handle(ret);
    };

    let remove = null;
    CapApp.addListener('appUrlOpen', ({ url }) => consume(url)).then((listener) => {
      remove = () => listener.remove();
    });

    if (claimLaunchRead()) {
      CapApp.getLaunchUrl?.()
        .then((res) => {
          if (res?.url) consume(res.url);
        })
        .catch(() => {});
    }

    return () => remove?.();
  }, [handle]);

  return { notice, pending, dismissNotice: () => setNotice(null) };
}

export default usePatreonReturn;
